import { useMemo, useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import Layout from "../Layout";
import SchemaFormRender from "@/components/Schema/SchemaFormRender";
import SchemaFormAutoSubmit from "@/components/Schema/SchemaFormAutoSubmit";
import { removeMetaFields } from "@/lib/removeMetaFields";
import { DynamicModuleService, SchemaService } from "@/services/APIs";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createForm } from "@formily/core";
import { notify } from "@/components/common/Notification";
import { toast } from "sonner";
import CommonModal from "@/components/common/CommonModal";

// Fields that are allowed to be edited
const EDITABLE_FIELDS = [
  "propertyname",
  "startingbid",
  "liens",
  "cash4keys",
  "tts",
  "maxbid",
  "propertynotes",
  "titlenotes",
];

// Check if a property is a layout component (Card, FormTab, FormLayout, etc.)
const isLayoutComponent = (property: any): boolean => {
  if (!property || typeof property !== "object") {
    return false;
  }

  // Layout components are typically void type with x-component
  const isVoid = property.type === "void";
  const hasXComponent =
    property["x-component"] && typeof property["x-component"] === "string";

  // Common layout components
  const layoutComponents = [
    "Card",
    "FormTab",
    "FormLayout",
    "FormGrid",
    "Tabs",
    "FormItem",
  ];
  const componentName = property["x-component"];

  return isVoid && hasXComponent && layoutComponents.includes(componentName);
};

// Helper function to reorder properties within a schema object
const reorderProperties = (
  properties: Record<string, any>
): Record<string, any> => {
  const keys = Object.keys(properties);
  const propertynameKey = keys.find(k => k.toLowerCase() === "propertyname");
  const startingbidKey = keys.find(k => k.toLowerCase() === "startingbid");

  if (!propertynameKey || !startingbidKey) {
    return properties;
  }

  const propertynameIndex = keys.indexOf(propertynameKey);
  const startingbidIndex = keys.indexOf(startingbidKey);

  if (propertynameIndex > startingbidIndex) {
    // Create new ordered array
    const reorderedKeys = [...keys];
    reorderedKeys.splice(propertynameIndex, 1);
    const newStartingbidIndex = reorderedKeys.indexOf(startingbidKey);
    reorderedKeys.splice(newStartingbidIndex, 0, propertynameKey);

    // Build new properties object with correct order and x-index
    const reordered: Record<string, any> = {};
    reorderedKeys.forEach((key, index) => {
      reordered[key] = {
        ...properties[key],
        "x-index": index,
      };
    });

    console.log("Reordered properties:", {
      original: keys,
      reordered: reorderedKeys,
      propertynameIndex,
      startingbidIndex,
    });

    return reordered;
  }

  return properties;
};

// Recursively modify schema to only show editable fields (hide all others)
const modifySchemaToRestrictFields = (schema: any): any => {
  if (!schema || typeof schema !== "object") {
    return schema;
  }

  // Handle schema with nested schema property
  if (schema.schema) {
    return {
      ...schema,
      schema: modifySchemaToRestrictFields(schema.schema),
    };
  }

  // If it's a property definition
  if (schema.properties) {
    const modifiedProperties: any = {};
    const orderedKeys: string[] = [];
    const processedProperties: Record<string, any> = {};

    // Sort keys to ensure propertyGrid comes before basicGrid
    const sortedKeys = Object.keys(schema.properties).sort((a, b) => {
      const aLower = a.toLowerCase();
      const bLower = b.toLowerCase();

      // propertyGrid should come first
      if (aLower === "propertygrid" || aLower === "property_grid") {
        return -1;
      }
      if (bLower === "propertygrid" || bLower === "property_grid") {
        return 1;
      }

      // basicGrid should come after propertyGrid
      if (aLower === "basicgrid" || aLower === "basic_grid") {
        return 1;
      }
      if (bLower === "basicgrid" || bLower === "basic_grid") {
        return -1;
      }

      // Keep original order for others
      return 0;
    });

    // First pass: collect all properties in sorted order
    sortedKeys.forEach(key => {
      const property = schema.properties[key];

      // Check if this is a layout component (Card, FormTab, etc.)
      if (isLayoutComponent(property)) {
        // Keep layout components and recursively process their properties
        let modifiedLayout = modifySchemaToRestrictFields(property);
        // Only include layout component if it has properties after filtering
        if (
          modifiedLayout?.properties &&
          Object.keys(modifiedLayout.properties).length > 0
        ) {
          // Reorder properties within layout component: propertyname before startingbid
          modifiedLayout = {
            ...modifiedLayout,
            properties: reorderProperties(modifiedLayout.properties),
          };

          processedProperties[key] = modifiedLayout;
          orderedKeys.push(key);
        }
      } else {
        const isEditable = EDITABLE_FIELDS.some(
          field => field.toLowerCase() === key.toLowerCase()
        );

        // Only include fields that are in the editable list
        if (isEditable) {
          const modifiedProperty = modifySchemaToRestrictFields(property);

          // Make finalarv field disabled (read-only, auto-calculated)
          if (key.toLowerCase() === "finalarv") {
            modifiedProperty["x-component-props"] = {
              ...modifiedProperty["x-component-props"],
              disabled: true,
              readOnly: true,
            };
          }

          // Make maxbid field disabled (read-only, auto-calculated)
          if (key.toLowerCase() === "maxbid") {
            modifiedProperty["x-component-props"] = {
              ...modifiedProperty["x-component-props"],
              disabled: true,
              readOnly: true,
            };
          }

          // Make startingbid field disabled (read-only)
          if (key.toLowerCase() === "startingbid") {
            modifiedProperty["x-component-props"] = {
              ...modifiedProperty["x-component-props"],
              disabled: true,
              readOnly: true,
            };
          }

          // Make propertyname field disabled (read-only)
          if (key.toLowerCase() === "propertyname") {
            modifiedProperty["x-component-props"] = {
              ...modifiedProperty["x-component-props"],
              disabled: true,
              readOnly: true,
            };
          }

          processedProperties[key] = modifiedProperty;
          orderedKeys.push(key);
        }
        // Completely exclude non-editable fields
      }
    });

    // Reorder layout components: propertyGrid (contains propertyname) should come before basicGrid (contains startingbid)
    // Also handle direct field reordering if they're at the top level
    const propertyGridKey = orderedKeys.find(
      k =>
        k.toLowerCase() === "propertygrid" ||
        k.toLowerCase() === "property_grid"
    );
    const basicGridKey = orderedKeys.find(
      k => k.toLowerCase() === "basicgrid" || k.toLowerCase() === "basic_grid"
    );

    // Reorder layout components
    if (propertyGridKey && basicGridKey) {
      const propertyGridIndex = orderedKeys.indexOf(propertyGridKey);
      const basicGridIndex = orderedKeys.indexOf(basicGridKey);

      if (propertyGridIndex > basicGridIndex) {
        // Remove propertyGrid from its current position
        orderedKeys.splice(propertyGridIndex, 1);
        // Insert it before basicGrid
        const newBasicGridIndex = orderedKeys.indexOf(basicGridKey);
        orderedKeys.splice(newBasicGridIndex, 0, propertyGridKey);
        console.log(
          "Reordered layout components: propertyGrid before basicGrid"
        );
      }
    }

    // Also handle direct field reordering if they're at the top level (not in layout components)
    const propertynameKey = orderedKeys.find(
      k => k.toLowerCase() === "propertyname"
    );
    const startingbidKey = orderedKeys.find(
      k => k.toLowerCase() === "startingbid"
    );

    if (propertynameKey && startingbidKey) {
      const propertynameIndex = orderedKeys.indexOf(propertynameKey);
      const startingbidIndex = orderedKeys.indexOf(startingbidKey);

      if (propertynameIndex > startingbidIndex) {
        // Remove propertyname from its current position
        orderedKeys.splice(propertynameIndex, 1);
        // Insert it before startingbid
        const newStartingbidIndex = orderedKeys.indexOf(startingbidKey);
        orderedKeys.splice(newStartingbidIndex, 0, propertynameKey);
        console.log(
          "Reordered top-level fields: propertyname before startingbid"
        );
      }
    }

    // Build final properties object in the desired order
    orderedKeys.forEach((key, index) => {
      modifiedProperties[key] = processedProperties[key];
      // Ensure x-index is set for all components (both layout and fields)
      if (modifiedProperties[key]) {
        modifiedProperties[key] = {
          ...modifiedProperties[key],
          "x-index": index,
        };
      }
    });

    console.log(
      "Final modified properties order:",
      Object.keys(modifiedProperties)
    );

    // Return modified schema with filtered and reordered properties
    return {
      ...schema,
      properties: modifiedProperties,
    };
  }

  // If it's an array of items
  if (schema.items) {
    return {
      ...schema,
      items: modifySchemaToRestrictFields(schema.items),
    };
  }

  // Recursively process other object properties
  const modified: any = { ...schema };
  Object.keys(schema).forEach(key => {
    if (
      key !== "properties" &&
      key !== "items" &&
      key !== "schema" &&
      typeof schema[key] === "object"
    ) {
      modified[key] = modifySchemaToRestrictFields(schema[key]);
    }
  });
  return modified;
};

const PP_EditComparisonProperty: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();

  const schemaName = "Property Master";
  const comparePropertySchemaName = "Compare Property";
  const rehabEstimateSchemaName = "Rehab Estimate";
  const maxBidSchemaName = "Max Bid";

  const form = useMemo(() => createForm({}), []);

  // Modal and comparison properties state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [comparisonProperties, setComparisonProperties] = useState<any[]>([]);
  const compareForm = useMemo(() => createForm({}), []);
  const compareFormRef = useRef<{ handleSave: () => Promise<void> }>(null);

  // Rehab estimate state
  const [rehabEstimateData, setRehabEstimateData] = useState<{
    rehabtype: string | null;
    rehabestimate: string | null;
  }>({
    rehabtype: null,
    rehabestimate: null,
  });

  // Local state for manual form fields
  const [formFields, setFormFields] = useState<{
    liens: string;
    cash4keys: string;
    tts: string;
    maxbid: string;
    propertynotes: string;
    titlenotes: string;
  }>({
    liens: "",
    cash4keys: "",
    tts: "",
    maxbid: "",
    propertynotes: "",
    titlenotes: "",
  });

  // Get schema columns first to know what fields to fetch
  const { data: columnGridData } = useQuery({
    queryKey: ["columnGridData", comparePropertySchemaName],
    queryFn: async () => {
      try {
        const result = await SchemaService.getColumnGridData(
          comparePropertySchemaName
        );
        return result ?? { grid: [] };
      } catch {
        return { grid: [] };
      }
    },
    enabled: !!comparePropertySchemaName,
  });

  // Load comparison properties from backend
  const {
    data: comparisonPropertiesData,
    refetch: refetchComparisonProperties,
  } = useQuery({
    queryKey: [
      "comparison-properties",
      comparePropertySchemaName,
      columnGridData,
      id,
    ],
    queryFn: async () => {
      if (!columnGridData?.grid || !id) {
        return { results: [], count: 0 };
      }

      // Get all column names from schema (all fields including system fields)
      // Keep original case for field names
      const allColumns = columnGridData.grid.map(
        (field: any) => field.field_name
      );

      // Explicitly add "id" if not already present (only once, lowercase)
      const lowerColumns = allColumns.map((col: string) => col.toLowerCase());
      if (!lowerColumns.includes("id")) {
        allColumns.push("id");
      }

      console.log("All columns from schema:", allColumns);

      // Fetch all comparison properties with all schema fields
      let response;
      try {
        response = await DynamicModuleService.getGridCoByConfig(
          comparePropertySchemaName,
          1,
          1000,
          allColumns, // Request all columns from schema
          {
            propertymaster_id: id,
          }
        );

        // If ID is not in the response, try fetching without column restrictions
        if (
          response?.results?.[0] &&
          !(
            "id" in response.results[0] ||
            "Id" in response.results[0] ||
            "ID" in response.results[0]
          )
        ) {
          console.warn(
            "ID not found in response, trying without column restrictions..."
          );
          try {
            const fullResponse = await DynamicModuleService.getGridCoByConfig(
              comparePropertySchemaName,
              1,
              1000,
              [], // Empty array to get all fields
              {
                propertymaster_id: id,
              }
            );
            if (
              fullResponse?.results?.[0] &&
              ("id" in fullResponse.results[0] ||
                "Id" in fullResponse.results[0] ||
                "ID" in fullResponse.results[0])
            ) {
              response = fullResponse;
            }
          } catch (fallbackError) {
            console.error("Error in fallback fetch:", fallbackError);
            // Continue with original response even if fallback fails
          }
        }
      } catch (error) {
        console.error("Error fetching comparison properties:", error);
        // Return empty result instead of throwing to prevent query from breaking
        return { results: [], count: 0 };
      }

      console.log("Comparison properties response:", response);
      console.log(
        "First item keys:",
        response?.results?.[0] ? Object.keys(response.results[0]) : "No items"
      );
      console.log("First item full data:", response?.results?.[0]);

      // Ensure we always return a valid response structure
      return response ?? { results: [], count: 0 };
    },
    enabled: !!comparePropertySchemaName && !!columnGridData && !!id,
  });

  // Update state when data is loaded
  useEffect(() => {
    if (comparisonPropertiesData) {
      // Handle different response structures
      const data =
        comparisonPropertiesData.results ??
        comparisonPropertiesData.data ??
        comparisonPropertiesData;

      let propertiesArray: any[] = [];
      if (Array.isArray(data)) {
        propertiesArray = data;
      } else if (data && Array.isArray(data.results)) {
        propertiesArray = data.results;
      } else {
        console.warn("Unexpected data structure:", comparisonPropertiesData);
        setComparisonProperties([]);
        return;
      }

      // Normalize all property objects to have lowercase keys for consistency
      const normalizedProperties = propertiesArray.map((property: any) => {
        const normalized: any = {};
        Object.keys(property).forEach(key => {
          normalized[key.toLowerCase()] = property[key];
        });
        return normalized;
      });

      console.log("Setting comparison properties:", normalizedProperties);
      setComparisonProperties(normalizedProperties);
    } else {
      setComparisonProperties([]);
    }
  }, [comparisonPropertiesData]);

  // Get rehab estimate schema columns
  const { data: rehabEstimateColumnData } = useQuery({
    queryKey: ["columnGridData", rehabEstimateSchemaName],
    queryFn: async () => {
      try {
        const result = await SchemaService.getColumnGridData(
          rehabEstimateSchemaName
        );
        return result ?? { grid: [] };
      } catch {
        return { grid: [] };
      }
    },
    enabled: !!rehabEstimateSchemaName,
  });

  // Fetch Rehab Estimate data filtered by propertymaster_id
  const { data: rehabEstimateResponse } = useQuery({
    queryKey: [
      "rehab-estimate",
      rehabEstimateSchemaName,
      id,
      rehabEstimateColumnData,
    ],
    queryFn: async () => {
      if (!rehabEstimateColumnData?.grid || !id) {
        return { results: [], count: 0 };
      }

      // Get all column names from schema
      const allColumns = rehabEstimateColumnData.grid.map(
        (field: any) => field.field_name
      );

      // Explicitly add "id" if not already present
      const lowerColumns = allColumns.map((col: string) => col.toLowerCase());
      if (!lowerColumns.includes("id")) {
        allColumns.push("id");
      }

      try {
        const response = await DynamicModuleService.getGridCoByConfig(
          rehabEstimateSchemaName,
          1,
          1000,
          allColumns,
          {
            propertymaster_id: id,
          }
        );

        console.log("Rehab Estimate response:", response);
        return response ?? { results: [], count: 0 };
      } catch (error) {
        console.error("Error fetching rehab estimate:", error);
        return { results: [], count: 0 };
      }
    },
    enabled: !!rehabEstimateSchemaName && !!rehabEstimateColumnData && !!id,
  });

  // Update rehab estimate data when loaded
  useEffect(() => {
    if (rehabEstimateResponse) {
      const data =
        rehabEstimateResponse.results ??
        rehabEstimateResponse.data ??
        rehabEstimateResponse;

      let rehabEstimateArray: any[] = [];
      if (Array.isArray(data)) {
        rehabEstimateArray = data;
      } else if (data && Array.isArray(data.results)) {
        rehabEstimateArray = data.results;
      }

      // Get the first rehab estimate record for this property
      if (rehabEstimateArray.length > 0) {
        const firstRecord = rehabEstimateArray[0];
        // Normalize keys to lowercase for consistency
        const normalized: any = {};
        Object.keys(firstRecord).forEach(key => {
          normalized[key.toLowerCase()] = firstRecord[key];
        });

        const rehabData = {
          rehabtype: normalized.rehabtype ?? null,
          rehabestimate: normalized.rehabestimate ?? null,
        };
        console.log("Rehab Estimate data loaded:", rehabData);
        setRehabEstimateData(rehabData);

        // Log for debugging - data is now available in rehabEstimateData state
        console.log(
          "Rehab Type:",
          rehabData.rehabtype,
          "Rehab Estimate:",
          rehabData.rehabestimate
        );
      } else {
        // Reset to null if no data found
        setRehabEstimateData({
          rehabtype: null,
          rehabestimate: null,
        });
      }
    } else {
      setRehabEstimateData({
        rehabtype: null,
        rehabestimate: null,
      });
    }
  }, [rehabEstimateResponse]);

  // Log rehab estimate data when it changes (for debugging)
  useEffect(() => {
    if (rehabEstimateData.rehabtype || rehabEstimateData.rehabestimate) {
      console.log("Rehab Estimate state updated:", rehabEstimateData);
    }
  }, [rehabEstimateData]);

  // Get Max Bid schema columns
  const { data: maxBidColumnData } = useQuery({
    queryKey: ["columnGridData", maxBidSchemaName],
    queryFn: async () => {
      try {
        const result = await SchemaService.getColumnGridData(maxBidSchemaName);
        return result ?? { grid: [] };
      } catch {
        return { grid: [] };
      }
    },
    enabled: !!maxBidSchemaName,
    refetchOnMount: true, // Refetch when component mounts
    staleTime: 0, // Consider data stale immediately
  });

  // Fetch existing Max Bid record
  const { data: maxBidResponse, refetch: refetchMaxBid } = useQuery({
    queryKey: ["max-bid", maxBidSchemaName, id, maxBidColumnData],
    queryFn: async () => {
      if (!maxBidColumnData?.grid || !id) {
        return { results: [], count: 0 };
      }

      const allColumns = maxBidColumnData.grid.map(
        (field: any) => field.field_name
      );

      const lowerColumns = allColumns.map((col: string) => col.toLowerCase());
      if (!lowerColumns.includes("id")) {
        allColumns.push("id");
      }

      // Check if propertymaster_id exists in schema before using it as filter
      const hasPropertyMasterId = lowerColumns.includes("propertymaster_id");

      try {
        const filter: any = {};
        if (hasPropertyMasterId) {
          filter.propertymaster_id = id;
        }

        const response = await DynamicModuleService.getGridCoByConfig(
          maxBidSchemaName,
          1,
          1000,
          allColumns,
          Object.keys(filter).length > 0 ? filter : undefined
        );

        console.log("Max Bid response:", response);
        return response ?? { results: [], count: 0 };
      } catch (error) {
        console.error("Error fetching max bid:", error);
        return { results: [], count: 0 };
      }
    },
    enabled: !!maxBidSchemaName && !!maxBidColumnData && !!id,
    refetchOnMount: true, // Refetch when component mounts
    refetchOnWindowFocus: false, // Don't refetch on window focus (optional)
    staleTime: 0, // Consider data stale immediately so it refetches
  });

  // Store Max Bid record ID
  const [maxBidRecordId, setMaxBidRecordId] = useState<string | null>(null);

  // Update Max Bid record ID when data is loaded
  useEffect(() => {
    if (maxBidResponse && maxBidColumnData?.grid) {
      const data =
        maxBidResponse.results ?? maxBidResponse.data ?? maxBidResponse;

      let maxBidArray: any[] = [];
      if (Array.isArray(data)) {
        maxBidArray = data;
      } else if (data && Array.isArray(data.results)) {
        maxBidArray = data.results;
      }

      if (maxBidArray.length > 0) {
        const firstRecord = maxBidArray[0];

        // Helper function to find field value by case-insensitive search
        const getFieldValue = (searchName: string): any => {
          // First try exact match
          if (firstRecord[searchName] !== undefined) {
            return firstRecord[searchName];
          }
          // Then try case-insensitive match
          const key = Object.keys(firstRecord).find(
            k => k.toLowerCase() === searchName.toLowerCase()
          );
          return key ? firstRecord[key] : undefined;
        };

        // Get record ID
        const recordId = getFieldValue("id") ?? getFieldValue("_id") ?? null;
        setMaxBidRecordId(recordId);

        // Load existing values into formFields using actual field names
        const updatedFields: any = {};

        // lienamount -> liens
        const lienamountValue = getFieldValue("lienamount");
        // Handle 0 values correctly - 0 is a valid number, only exclude undefined/null
        if (lienamountValue !== undefined && lienamountValue !== null) {
          updatedFields.liens = String(lienamountValue);
        } else {
          // Explicitly set to empty string if not found
          updatedFields.liens = "";
        }

        // cash4keys
        const cash4keysValue = getFieldValue("cash4keys");
        // Handle 0 values correctly - 0 is a valid number, only exclude undefined/null
        if (cash4keysValue !== undefined && cash4keysValue !== null) {
          updatedFields.cash4keys = String(cash4keysValue);
        } else {
          // Explicitly set to empty string if not found
          updatedFields.cash4keys = "";
        }

        // TTS (can be "tts" or "TTS")
        const ttsValue = getFieldValue("TTS") ?? getFieldValue("tts");
        // Handle 0 values correctly - 0 is a valid number, only exclude undefined/null
        if (ttsValue !== undefined && ttsValue !== null) {
          updatedFields.tts = String(ttsValue);
        } else {
          // Explicitly set to empty string if not found
          updatedFields.tts = "";
        }

        console.log("Loading formFields from database:", {
          lienamountValue,
          cash4keysValue,
          ttsValue,
          updatedFields,
        });

        // maxbid
        const maxbidValue = getFieldValue("maxbid");
        if (maxbidValue !== undefined && maxbidValue !== null) {
          updatedFields.maxbid = String(maxbidValue);
        }

        // propertynotes
        const propertynotesValue = getFieldValue("propertynotes");
        if (propertynotesValue !== undefined && propertynotesValue !== null) {
          updatedFields.propertynotes = String(propertynotesValue);
        }

        // titlenotes
        const titlenotesValue = getFieldValue("titlenotes");
        if (titlenotesValue !== undefined && titlenotesValue !== null) {
          updatedFields.titlenotes = String(titlenotesValue);
        }

        // Update formFields
        setFormFields(prev => ({
          ...prev,
          ...updatedFields,
        }));

        // Load startingbid and propertyname into form
        const startingbidValue = getFieldValue("startingbid");
        if (startingbidValue !== undefined && startingbidValue !== null) {
          form.setFieldState("startingbid", state => {
            state.value = startingbidValue;
          });
        }

        const propertynameValue = getFieldValue("propertyname");
        if (propertynameValue !== undefined && propertynameValue !== null) {
          form.setFieldState("propertyname", state => {
            state.value = propertynameValue;
          });
        }

        // Load finalarv into form
        const finalarvValue = getFieldValue("finalarv");
        if (finalarvValue !== undefined && finalarvValue !== null) {
          form.setFieldState("finalarv", state => {
            state.value = finalarvValue;
          });
        }

        console.log("Loaded Max Bid data:", {
          recordId,
          startingbid: startingbidValue,
          propertyname: propertynameValue,
          finalarv: finalarvValue,
          ...updatedFields,
        });
      } else {
        setMaxBidRecordId(null);
        console.log("No Max Bid record found");
      }
    } else if (maxBidResponse && !maxBidColumnData) {
      // If we have response but no column data yet, wait for it
      console.log("Waiting for column data to load Max Bid record");
    } else {
      setMaxBidRecordId(null);
    }
  }, [maxBidResponse, maxBidColumnData, form]);

  // Refetch Max Bid data when component mounts or when id/columnData changes
  useEffect(() => {
    if (maxBidSchemaName && maxBidColumnData && id) {
      console.log("Refetching Max Bid data on mount/change");
      refetchMaxBid();
    }
  }, [id, maxBidSchemaName, maxBidColumnData, refetchMaxBid]);

  const { data: schemaJson } = useQuery({
    queryKey: ["schema", schemaName],
    queryFn: () => SchemaService.getSchemaByName(schemaName ?? ""),
    enabled: !!schemaName,
  });

  const { data: recordInfo } = useQuery({
    queryKey: ["dynamic-model", schemaName, id],
    queryFn: () => DynamicModuleService.getItemById(schemaName ?? "", id ?? ""),
    enabled: !!schemaName && !!id,
  });

  // Modify schema to restrict fields
  const restrictedSchema = useMemo(() => {
    if (!schemaJson) {
      return null;
    }
    const schema = schemaJson.json ?? schemaJson;

    const modified = modifySchemaToRestrictFields(schema);

    // Force propertyGrid to come before basicGrid by rebuilding properties object in correct order
    if (
      modified &&
      typeof modified === "object" &&
      "properties" in modified &&
      modified.properties
    ) {
      const props = modified.properties;
      const keys = Object.keys(props);

      // Find propertyGrid and basicGrid keys
      const propertyGridKey = keys.find(
        k =>
          k.toLowerCase() === "propertygrid" ||
          k.toLowerCase() === "property_grid"
      );
      const basicGridKey = keys.find(
        k => k.toLowerCase() === "basicgrid" || k.toLowerCase() === "basic_grid"
      );

      // If both exist, rebuild properties object with propertyGrid first
      if (propertyGridKey && basicGridKey) {
        const propertyGridIndex = keys.indexOf(propertyGridKey);
        const basicGridIndex = keys.indexOf(basicGridKey);

        if (propertyGridIndex > basicGridIndex) {
          // Rebuild properties object with correct order
          const reorderedProps: any = {};

          // Add propertyGrid first
          reorderedProps[propertyGridKey] = {
            ...props[propertyGridKey],
            "x-index": 0,
          };

          // Add basicGrid second
          reorderedProps[basicGridKey] = {
            ...props[basicGridKey],
            "x-index": 1,
          };

          // Add all other properties in their original order (but after propertyGrid and basicGrid)
          let currentIndex = 2;
          keys.forEach(key => {
            if (key !== propertyGridKey && key !== basicGridKey) {
              reorderedProps[key] = {
                ...props[key],
                "x-index": currentIndex++,
              };
            }
          });

          modified.properties = reorderedProps;
          console.log(
            "Rebuilt properties with propertyGrid before basicGrid:",
            Object.keys(reorderedProps)
          );
        }
      }
    }

    return modified;
  }, [schemaJson]);

  // Set initial values when record is loaded
  // Note: Only initialize from recordInfo if Max Bid data hasn't been loaded yet
  // Max Bid data should take precedence since it's the source of truth for these fields
  useEffect(() => {
    if (recordInfo && !maxBidResponse) {
      const initialData = removeMetaFields(recordInfo);
      form.setInitialValues(initialData);
      // Set current values to ensure form fields are populated
      Object.keys(initialData).forEach(key => {
        form.setFieldState(key, state => {
          state.value = initialData[key];
        });
      });

      // Initialize local form fields from recordInfo only if Max Bid data not loaded
      // Max Bid data will override these values when it loads
      setFormFields(prev => ({
        liens: prev.liens ?? String(initialData.liens ?? ""),
        cash4keys: prev.cash4keys ?? String(initialData.cash4keys ?? ""),
        tts: prev.tts ?? String(initialData.tts ?? ""),
        maxbid: prev.maxbid ?? String(initialData.maxbid ?? ""),
        propertynotes:
          prev.propertynotes ?? String(initialData.propertynotes ?? ""),
        titlenotes: prev.titlenotes ?? String(initialData.titlenotes ?? ""),
      }));
    } else if (recordInfo) {
      // If recordInfo loads but Max Bid data exists, only set form values, not formFields
      const initialData = removeMetaFields(recordInfo);
      form.setInitialValues(initialData);
      // Set current values to ensure form fields are populated
      Object.keys(initialData).forEach(key => {
        form.setFieldState(key, state => {
          state.value = initialData[key];
        });
      });
    }
  }, [recordInfo, form, maxBidResponse]);

  // Disable startingbid and propertyname fields
  useEffect(() => {
    form.setFieldState("startingbid", state => {
      state.componentProps = {
        ...state.componentProps,
        disabled: true,
        readOnly: true,
      };
    });
    form.setFieldState("propertyname", state => {
      state.componentProps = {
        ...state.componentProps,
        disabled: true,
        readOnly: true,
      };
    });
  }, [form]);

  // Update finalarv and maxbid fields in form when values change
  useEffect(() => {
    const calculatedARV = calculateARV();
    if (calculatedARV !== null) {
      form.setFieldState("finalarv", state => {
        state.value = calculatedARV.toString();
      });
    }

    // Calculate and update Max Bid
    const calculatedMaxBid = calculateMaxBid();
    if (calculatedMaxBid !== null) {
      const maxBidStr = calculatedMaxBid.toString();
      form.setFieldState("maxbid", state => {
        state.value = maxBidStr;
      });
      // Also update local state
      setFormFields(prev => ({ ...prev, maxbid: maxBidStr }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    comparisonProperties,
    recordInfo,
    form,
    rehabEstimateData,
    formFields.liens,
    formFields.cash4keys,
    formFields.tts,
    // calculateARV and calculateMaxBid are defined later in the component
    // but their dependencies (recordInfo, comparisonProperties, form, etc.) are already included
  ]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!id) {
        notify.error("Property ID missing. Please reopen the page.");
        return;
      }

      // Calculate ARV and Max Bid
      const calculatedARV = calculateARV();
      const calculatedMaxBid = calculateMaxBid();

      // Create a helper function to find field name in schema (case-insensitive)
      const findFieldName = (searchName: string): string | null => {
        if (!maxBidColumnData?.grid) {
          return null;
        }
        const field = maxBidColumnData.grid.find((field: any) => {
          const fieldName = field.field_name?.toLowerCase();
          return fieldName === searchName.toLowerCase();
        });
        return field?.field_name ?? null;
      };

      // Prepare all 11 fields for Max Bid schema using actual field names from schema
      const maxBidData: any = {};

      // propertymaster_id
      const propertymasterIdField = findFieldName("propertymaster_id");
      if (propertymasterIdField) {
        maxBidData[propertymasterIdField] = id;
      }

      // startingbid
      const startingbidField = findFieldName("startingbid");
      if (
        startingbidField &&
        form.values.startingbid !== undefined &&
        form.values.startingbid !== null &&
        form.values.startingbid !== ""
      ) {
        const startingbidValue =
          typeof form.values.startingbid === "string"
            ? parseFloat(form.values.startingbid)
            : form.values.startingbid;
        if (!isNaN(startingbidValue)) {
          maxBidData[startingbidField] = startingbidValue;
        }
      }

      // propertyname
      const propertynameField = findFieldName("propertyname");
      if (
        propertynameField &&
        form.values.propertyname !== undefined &&
        form.values.propertyname !== null &&
        form.values.propertyname !== ""
      ) {
        maxBidData[propertynameField] = form.values.propertyname;
      }

      // finalarv
      const finalarvField = findFieldName("finalarv");
      if (finalarvField && calculatedARV !== null) {
        maxBidData[finalarvField] = calculatedARV;
      }

      // lienamount (from formFields.liens)
      const lienamountField = findFieldName("lienamount");
      if (
        lienamountField &&
        formFields.liens !== undefined &&
        formFields.liens !== null &&
        formFields.liens !== ""
      ) {
        const liensValue = parseFloat(formFields.liens);
        if (!isNaN(liensValue)) {
          maxBidData[lienamountField] = liensValue;
        } else {
          // Save as 0 if invalid number
          maxBidData[lienamountField] = 0;
        }
      } else if (lienamountField) {
        // Explicitly save 0 or null if field exists but value is empty
        maxBidData[lienamountField] = 0;
      }

      // cash4keys
      const cash4keysField = findFieldName("cash4keys");
      if (
        cash4keysField &&
        formFields.cash4keys !== undefined &&
        formFields.cash4keys !== null &&
        formFields.cash4keys !== ""
      ) {
        const cash4keysValue = parseFloat(formFields.cash4keys);
        if (!isNaN(cash4keysValue)) {
          maxBidData[cash4keysField] = cash4keysValue;
        } else {
          // Save as 0 if invalid number
          maxBidData[cash4keysField] = 0;
        }
      } else if (cash4keysField) {
        // Explicitly save 0 or null if field exists but value is empty
        maxBidData[cash4keysField] = 0;
      }

      // TTS (can be "tts" or "TTS" in schema)
      const ttsField = findFieldName("tts") ?? findFieldName("TTS");
      if (
        ttsField &&
        formFields.tts !== undefined &&
        formFields.tts !== null &&
        formFields.tts !== ""
      ) {
        const ttsValue = parseFloat(formFields.tts);
        if (!isNaN(ttsValue)) {
          maxBidData[ttsField] = ttsValue;
        } else {
          // Save as 0 if invalid number
          maxBidData[ttsField] = 0;
        }
      } else if (ttsField) {
        // Explicitly save 0 or null if field exists but value is empty
        maxBidData[ttsField] = 0;
      }

      // maxbid
      const maxbidField = findFieldName("maxbid");
      if (maxbidField && calculatedMaxBid !== null) {
        maxBidData[maxbidField] = calculatedMaxBid;
      }

      // rehabtype
      const rehabtypeField = findFieldName("rehabtype");
      if (
        rehabtypeField &&
        rehabEstimateData.rehabtype !== undefined &&
        rehabEstimateData.rehabtype !== null &&
        rehabEstimateData.rehabtype !== ""
      ) {
        const rehabtypeValue =
          typeof rehabEstimateData.rehabtype === "string"
            ? parseFloat(rehabEstimateData.rehabtype)
            : rehabEstimateData.rehabtype;
        if (!isNaN(rehabtypeValue)) {
          maxBidData[rehabtypeField] = rehabtypeValue;
        } else {
          maxBidData[rehabtypeField] = rehabEstimateData.rehabtype;
        }
      }

      // rehabestimate
      const rehabestimateField = findFieldName("rehabestimate");
      if (rehabestimateField && rehabEstimateData.rehabestimate) {
        const rehabEstimateValue = parseFloat(
          String(rehabEstimateData.rehabestimate)
        );
        if (!isNaN(rehabEstimateValue)) {
          maxBidData[rehabestimateField] = rehabEstimateValue;
        }
      }

      // propertynotes
      const propertynotesField = findFieldName("propertynotes");
      if (
        propertynotesField &&
        formFields.propertynotes &&
        formFields.propertynotes !== ""
      ) {
        maxBidData[propertynotesField] = formFields.propertynotes;
      }

      // titlenotes
      const titlenotesField = findFieldName("titlenotes");
      if (
        titlenotesField &&
        formFields.titlenotes &&
        formFields.titlenotes !== ""
      ) {
        maxBidData[titlenotesField] = formFields.titlenotes;
      }

      // Log all available schema fields for debugging
      if (maxBidColumnData?.grid) {
        console.log(
          "Available schema fields:",
          maxBidColumnData.grid.map((f: any) => f.field_name)
        );
      }

      console.log("Saving Max Bid data:", maxBidData);
      console.log("Max Bid Record ID:", maxBidRecordId);
      console.log("Max Bid Schema Name:", maxBidSchemaName);

      // Save or update Max Bid record
      let response: any;
      if (maxBidRecordId) {
        // Update existing record
        console.log("Updating existing Max Bid record...");
        response = await DynamicModuleService.updateItem(
          maxBidSchemaName,
          maxBidRecordId,
          maxBidData
        );
        console.log("Update response:", response);

        // Check if update was successful
        // Note: axiosUtils returns undefined on error, and the axios interceptor already shows the error notification
        if (!response) {
          // Error was already shown by axios interceptor, just return
          return;
        }

        if (response && !response.error) {
          toast.success("Max Bid updated successfully!");
        } else {
          const errorMsg = response?.message ?? "Failed to update Max Bid";
          notify.error(errorMsg);
          return;
        }
      } else {
        // Create new record
        console.log("Creating new Max Bid record...");
        response = await DynamicModuleService.createItem(
          maxBidSchemaName,
          maxBidData
        );
        console.log("Create response:", response);

        // Check if create was successful
        // Note: axiosUtils returns undefined on error, and the axios interceptor already shows the error notification
        if (!response) {
          // Error was already shown by axios interceptor, just return
          return;
        }

        if (
          response &&
          !response.error &&
          (response?.id ?? response?.Id ?? response?.ID ?? response?.data?.id)
        ) {
          const newId =
            response.id ?? response.Id ?? response.ID ?? response.data?.id;
          setMaxBidRecordId(newId);
          toast.success("Max Bid saved successfully!");
        } else {
          const errorMsg =
            response?.message ?? response?.error ?? "Failed to create Max Bid";
          notify.error(errorMsg);
          return;
        }
      }

      // Refetch the max bid data immediately to ensure it's updated
      await refetchMaxBid();

      // Invalidate queries to refresh data
      await queryClient.invalidateQueries({
        queryKey: ["max-bid", maxBidSchemaName, id],
      });
      await queryClient.invalidateQueries({
        queryKey: ["columnGridData", maxBidSchemaName],
      });

      // Small delay before navigation to ensure data is saved and refetched
      setTimeout(() => {
        navigate("/property-portal/max-bid-calculation");
      }, 500);
    } catch (error: any) {
      console.error("Save error:", error);
      console.error("Error details:", {
        message: error?.message,
        response: error?.response,
        data: error?.response?.data,
      });
      const errorMessage =
        error?.response?.data?.message ??
        error?.response?.data?.error ??
        error?.message ??
        error?.error ??
        "Failed to save Max Bid. Please check the console for details.";
      notify.error(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleCancel = () => {
    navigate("/property-portal/max-bid-calculation");
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    compareForm.reset();
  };

  // Helper function to save ARV to Property Master
  const saveARVToPropertyMaster = async () => {
    if (!id || !recordInfo) {
      return;
    }

    try {
      const calculatedARV = calculateARV();
      if (calculatedARV !== null) {
        const originalData = removeMetaFields(recordInfo);
        const updateData = {
          ...originalData,
          finalarv: calculatedARV.toString(),
        };

        await DynamicModuleService.updateItem(schemaName, id, updateData);

        // Refetch record info to get updated ARV
        await queryClient.invalidateQueries({
          queryKey: ["dynamic-model", schemaName, id],
        });
      }
    } catch (error) {
      console.error("Error saving ARV to Property Master:", error);
      // Don't show error to user as this is a background operation
    }
  };

  const handleComparePropertySave = async () => {
    try {
      // Validate form
      await compareForm.validate();

      // Get the form values
      const savedData = removeMetaFields(compareForm.values);
      if (!id) {
        notify.error("Property ID missing. Please reopen the page.");
        return;
      }

      // Save to backend - no linking, just save directly
      await DynamicModuleService.createItem(comparePropertySchemaName, {
        ...savedData,
        propertymaster_id: id,
      });

      toast.success("Comparison property added successfully!");
      setIsModalOpen(false);
      compareForm.reset();

      // Invalidate and refetch comparison properties to update the table
      queryClient.invalidateQueries({
        queryKey: ["comparison-properties", comparePropertySchemaName],
      });
      await refetchComparisonProperties();

      // Recalculate and save ARV after adding comparison property
      await saveARVToPropertyMaster();
    } catch (error) {
      console.error("Save error:", error);
      notify.error(
        "Failed to save comparison property. Please check the form."
      );
    }
  };

  // Calculate average Per Sqft Price (persqftprice) including main property
  const calculateAvgPPSP = () => {
    if (!recordInfo) {
      return null;
    }

    // Get main property's persqftprice (case-insensitive search)
    const mainPropertyPersqftKey = Object.keys(recordInfo ?? {}).find(
      key => key.toLowerCase() === "persqftprice"
    );

    if (!mainPropertyPersqftKey) {
      return null;
    }

    const mainPropertyPersqftValue = recordInfo[mainPropertyPersqftKey];
    const mainPropertyPersqftNum =
      typeof mainPropertyPersqftValue === "string"
        ? parseFloat(mainPropertyPersqftValue)
        : mainPropertyPersqftValue;

    if (
      typeof mainPropertyPersqftNum !== "number" ||
      isNaN(mainPropertyPersqftNum)
    ) {
      return null;
    }

    // Find the field name for persqftprice in comparison properties (case-insensitive)
    const persqftpriceKey =
      comparisonProperties.length > 0
        ? Object.keys(comparisonProperties[0] ?? {}).find(
            key => key.toLowerCase() === "persqftprice"
          )
        : null;

    // Collect all valid prices: main property + comparison properties
    const validPrices: number[] = [mainPropertyPersqftNum];

    if (persqftpriceKey && comparisonProperties.length > 0) {
      comparisonProperties.forEach(property => {
        const value = property[persqftpriceKey];
        const numValue = typeof value === "string" ? parseFloat(value) : value;
        if (typeof numValue === "number" && !isNaN(numValue)) {
          validPrices.push(numValue);
        }
      });
    }

    if (validPrices.length === 0) {
      return null;
    }

    // Calculate average
    const sum = validPrices.reduce((acc, price) => acc + price, 0);
    const average = sum / validPrices.length;

    return average;
  };

  // Calculate ARV = avgPPSP * sqfootage (from main property)
  const calculateARV = () => {
    const avgPPSP = calculateAvgPPSP();
    if (avgPPSP === null || !recordInfo) {
      return null;
    }

    // Get main property's sqfootage (case-insensitive search)
    const sqfootageKey = Object.keys(recordInfo ?? {}).find(
      key => key.toLowerCase() === "sqfootage"
    );

    if (!sqfootageKey) {
      return null;
    }

    const sqfootageValue = recordInfo[sqfootageKey];
    const sqfootageNum =
      typeof sqfootageValue === "string"
        ? parseFloat(sqfootageValue)
        : sqfootageValue;

    if (typeof sqfootageNum !== "number" || isNaN(sqfootageNum)) {
      return null;
    }

    // Calculate ARV = average PPSP * sqfootage
    const arv = avgPPSP * sqfootageNum;

    return arv;
  };

  // Calculate Max Bid = ARV × 0.65 - (Rehab Estimate + Liens + Cash for Keys + TTS)
  const calculateMaxBid = () => {
    // Get ARV from finalarv field or calculate it
    let arv: number | null = null;

    // Try to get ARV from form values first
    const finalarvValue = form.values.finalarv;
    if (finalarvValue) {
      const arvNum =
        typeof finalarvValue === "string"
          ? parseFloat(finalarvValue)
          : finalarvValue;
      if (typeof arvNum === "number" && !isNaN(arvNum)) {
        arv = arvNum;
      }
    }

    // If not in form, try to get from recordInfo
    if (arv === null && recordInfo) {
      const finalarvKey = Object.keys(recordInfo ?? {}).find(
        key => key.toLowerCase() === "finalarv"
      );
      if (finalarvKey) {
        const arvValue = recordInfo[finalarvKey];
        const arvNum =
          typeof arvValue === "string" ? parseFloat(arvValue) : arvValue;
        if (typeof arvNum === "number" && !isNaN(arvNum)) {
          arv = arvNum;
        }
      }
    }

    // If still null, calculate ARV
    arv ??= calculateARV();

    if (arv === null) {
      return null;
    }

    // Get Rehab Estimate
    const rehabEstimate = rehabEstimateData.rehabestimate
      ? parseFloat(String(rehabEstimateData.rehabestimate))
      : 0;
    const rehabEstimateNum = isNaN(rehabEstimate) ? 0 : rehabEstimate;

    // Get Liens from local state
    const liens = formFields.liens ? parseFloat(formFields.liens) : 0;
    const liensNum = isNaN(liens) ? 0 : liens;

    // Get Cash for Keys from local state
    const cash4keys = formFields.cash4keys
      ? parseFloat(formFields.cash4keys)
      : 0;
    const cash4keysNum = isNaN(cash4keys) ? 0 : cash4keys;

    // Get TTS from local state
    const tts = formFields.tts ? parseFloat(formFields.tts) : 0;
    const ttsNum = isNaN(tts) ? 0 : tts;

    // Calculate Max Bid = ARV × 0.65 - (Rehab Estimate + Liens + Cash for Keys + TTS)
    const maxBid =
      arv * 0.65 - (rehabEstimateNum + liensNum + cash4keysNum + ttsNum);

    return maxBid;
  };

  return (
    <Layout>
      <div className="max-w-5xl space-y-4 lg:space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground lg:text-3xl">
            Calculate Max Bid
          </h1>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          {/* Property Information */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>Property Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <h3 className="text-sm font-semibold">Basic Information</h3>
                <SchemaFormRender form={form} schemaJson={restrictedSchema} />
              </div>
            </CardContent>
          </Card>

          {/* Financial Details */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>Financial Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="finalArv">Final ARV</Label>
                  <Input
                    id="finalArv"
                    name="finalArv"
                    type="number"
                    value={
                      form.values.finalarv ? String(form.values.finalarv) : ""
                    }
                    disabled
                    placeholder="Final ARV"
                    className="bg-muted"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Max Bid Calculation Fields */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>Max Bid Calculation Fields</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="liens">Liens</Label>
                  <Input
                    id="liens"
                    name="liens"
                    type="number"
                    value={formFields.liens}
                    onChange={e => {
                      setFormFields(prev => ({
                        ...prev,
                        liens: e.target.value,
                      }));
                    }}
                    placeholder="e.g., 5000"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cash4keys">Cash4Keys</Label>
                  <Input
                    id="cash4keys"
                    name="cash4keys"
                    type="number"
                    value={formFields.cash4keys}
                    onChange={e => {
                      setFormFields(prev => ({
                        ...prev,
                        cash4keys: e.target.value,
                      }));
                    }}
                    placeholder="e.g., 3000"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tts">TTS</Label>
                  <Input
                    id="tts"
                    name="tts"
                    type="number"
                    value={formFields.tts}
                    onChange={e => {
                      setFormFields(prev => ({
                        ...prev,
                        tts: e.target.value,
                      }));
                    }}
                    placeholder="e.g., 2000"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="maxBid">Max Bid</Label>
                  <Input
                    id="maxBid"
                    name="maxBid"
                    type="number"
                    value={formFields.maxbid}
                    disabled
                    placeholder="Calculated automatically"
                    className="bg-muted"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="propertyNotes">Property Notes</Label>
                  <Textarea
                    id="propertyNotes"
                    name="propertyNotes"
                    value={formFields.propertynotes}
                    onChange={e => {
                      setFormFields(prev => ({
                        ...prev,
                        propertynotes: e.target.value,
                      }));
                    }}
                    placeholder="Additional property notes..."
                    rows={4}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="titleNotes">Title Notes</Label>
                  <Textarea
                    id="titleNotes"
                    name="titleNotes"
                    value={formFields.titlenotes}
                    onChange={e => {
                      setFormFields(prev => ({
                        ...prev,
                        titlenotes: e.target.value,
                      }));
                    }}
                    placeholder="Additional title notes..."
                    rows={4}
                  />
                </div>
              </div>

              {(() => {
                // Display Rehab Estimate data if available
                if (
                  rehabEstimateData.rehabtype ||
                  rehabEstimateData.rehabestimate
                ) {
                  return (
                    <div className="mt-4 space-y-3">
                      {rehabEstimateData.rehabtype && (
                        <div className="rounded-lg border bg-muted/50 p-4">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold">Rehab Type:</span>
                            <span className="text-lg font-bold">
                              {rehabEstimateData.rehabtype}
                            </span>
                          </div>
                        </div>
                      )}
                      {rehabEstimateData.rehabestimate && (
                        <div className="rounded-lg border bg-muted/50 p-4">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold">
                              Rehab Estimate:
                            </span>
                            <span className="text-lg font-bold">
                              {rehabEstimateData.rehabestimate}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }
                return null;
              })()}
            </CardContent>
          </Card>

          <div className="flex gap-4">
            <Button
              type="submit"
              className="bg-[#4a5273] text-white hover:bg-[#4a5273]/90"
            >
              Save
            </Button>
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </form>

        {/* Compare Property Modal */}
        <CommonModal
          open={isModalOpen}
          onCancel={handleModalCancel}
          title="Add Comparison Property"
          width={800}
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleModalCancel}>
                Cancel
              </Button>
              <Button onClick={handleComparePropertySave}>Save</Button>
            </div>
          }
          destroyOnClose
        >
          <div className="mt-4">
            <SchemaFormAutoSubmit
              form={compareForm}
              schemaName={comparePropertySchemaName}
              ref={compareFormRef}
            />
          </div>
        </CommonModal>
      </div>
    </Layout>
  );
};

export default PP_EditComparisonProperty;
